<script setup lang="ts">
import { ref, watch } from 'vue';
import type { InputInst } from 'naive-ui';
import { useVaultStore, type VaultItem } from '@/stores/vaultStore';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import { gaiaAccent } from '@/components/design/themes/tokens';

// Props
const props = defineProps<{
  show: boolean;
  item: VaultItem | null;
}>();

// Emits
const emit = defineEmits<{
  'update:show': [value: boolean];
  renamed: [];
}>();

// Store
const vaultStore = useVaultStore();

// Local state
const newName = ref<string>('');
const isSubmitting = ref<boolean>(false);
const errorMessage = ref<string>('');
const newNameInput = ref<InputInst | null>(null);

// Watch for item changes
watch(
  () => props.item,
  newItem => {
    if (newItem) {
      newName.value = newItem.name;
    }
  }
);

// Methods
// Focus the name field once the modal has finished opening (replaces the
// `autofocus` prop, which the a11y linter disallows).
function focusNewNameInput(): void {
  newNameInput.value?.focus();
}

function closeModal(): void {
  emit('update:show', false);
  resetForm();
}

function resetForm(): void {
  newName.value = '';
  errorMessage.value = '';
  isSubmitting.value = false;
}

async function handleSubmit(): Promise<void> {
  if (!props.item) return;

  // Validate
  if (!newName.value.trim()) {
    errorMessage.value = 'Name is required';
    return;
  }

  isSubmitting.value = true;
  errorMessage.value = '';

  try {
    if (props.item.type === 'directory') {
      const directoryUid = props.item.key.replace('dir-', '');
      await vaultStore.updateDirectory(directoryUid, { name: newName.value });
    } else {
      const fileUid = props.item.key.replace('file-', '');
      await vaultStore.updateFile(fileUid, { name: newName.value });
    }

    emit('renamed');
    closeModal();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to rename item';
  } finally {
    isSubmitting.value = false;
  }
}

/**
 * Inline custom properties on the modal card rather than `v-bind()`. This
 * component's root is a Teleport, so Vue has no root element in place to
 * write `v-bind()` vars onto and every colour bound that way resolved to
 * nothing. On the card they cascade to header, body and footer alike.
 */
const styleVars = {
  '--vren-accent-color': gaiaAccent.base,
  '--vren-mark-surface': gaiaAccent.wash,
};
</script>

<template>
  <gaia-modal
    :style="styleVars"
    :show="props.show"
    width="440px"
    :mask-closable="false"
    @update:show="(value: boolean) => emit('update:show', value)"
    @after-enter="focusNewNameInput"
  >
    <template #header>
      <n-flex align="center" :size="11" :wrap="false">
        <div class="mark">
          <svg
            width="15"
            height="15"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M2.5 12.5 12 3l1 1-9.5 9.5-1.5.5z" />
          </svg>
        </div>
        <n-flex vertical :size="1" :style="{ minWidth: 0 }">
          <gaia-text size="subtitle" weight="semibold" tone="ink">
            Rename {{ props.item?.type === 'directory' ? 'folder' : 'file' }}
          </gaia-text>
          <gaia-text v-if="props.item" truncate size="meta" tone="subtle">
            {{ props.item.name }}
          </gaia-text>
        </n-flex>
      </n-flex>
    </template>

    <gaia-form-field
      v-if="props.item"
      :label="props.item.type === 'directory' ? 'Folder name' : 'File name'"
      required
      :error="errorMessage || undefined"
    >
      <gaia-input
        ref="newNameInput"
        v-model:value="newName"
        placeholder="Enter new name"
        style="width: 100%"
        @keyup.enter="handleSubmit"
      />
    </gaia-form-field>

    <template #footer>
      <n-flex justify="end" :size="8">
        <gaia-button size="small" @click="closeModal">Cancel</gaia-button>
        <gaia-button
          type="primary"
          size="small"
          :loading="isSubmitting"
          :disabled="!newName.trim() || !props.item"
          @click="handleSubmit"
        >
          Rename
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>

<style scoped>
.mark {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: var(--vren-mark-surface);
  color: var(--vren-accent-color);
}
</style>
